import { useFunnel } from "@airhelp/funnel";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import { InfoBox } from "@airhelp/react";
import { Info } from "@airhelp/icons";
import TranslationWithHTML from "../../../../components/ui-elements/translation-with-html/TranslationWithHTML";
import { useForm, useFormHandlers } from "../../../modules/form";
import { z } from "zod";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import { sendWarningReport } from "../../../../utils/reporting";
import {
  updateClaimDetails,
  updateItineraryFlights,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { ItineraryDetailsControl } from "v2/modules/form/ItineraryDetailsControl";
import { airlineSchema } from "./MultilegDisruptedFlight";
import { fetchPotentialRegulation } from "../../../api/potential-regulation";
import { airportSchema } from "./FlightDetails";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { isValidFlightNumber } from "validators/zod";

const itineraryFlightEmptySchema = z.object({
  disrupted: z.boolean(),
  flightNumber: z.string().nullable(),
  flightDate: z.date().nullable(),
  departureAirport: airportSchema.nullable(),
  arrivalAirport: airportSchema.nullable(),
  airline: airlineSchema.nullable(),
});

const itineraryFlightSchema = z.object({
  disrupted: z.boolean(),
  flightNumber: isValidFlightNumber(z.string()),
  flightDate: z.date(),
  departureAirport: airportSchema,
  arrivalAirport: airportSchema,
  airline: airlineSchema,
});

const defaultStepSchema = z.object({
  itineraryFlights: z.array(itineraryFlightEmptySchema),
});

const stepSchema = z
  .object({
    itineraryFlights: z.array(itineraryFlightSchema),
  })
  .refine(
    (data) => {
      const minDate = data.itineraryFlights[0]?.flightDate;
      if (minDate) {
        return data.itineraryFlights.every(
          (flight) => flight.flightDate && flight.flightDate >= minDate,
        );
      }
      return false;
    },
    {
      message: "All dates should be older than first selected",
    },
  );

export function ItineraryDetailsStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { itineraryFlights } = usePreEligibilityDataSnapshot();
  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      itineraryFlights: Array.from(itineraryFlights),
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ itineraryFlights }) {
      updateItineraryFlights(itineraryFlights);

      try {
        const potentialRegulation = await fetchPotentialRegulation(itineraryFlights);
        updateClaimDetails({ potentialRegulation });
      } catch (e) {
        updateClaimDetails({ potentialRegulation: "ec261" });
        sendWarningReport("Failed to fetch potential regulation, `ec261` was used as fallback", e);
      }

      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      trackEnquiryStepComplete();
      funnel.stepForward();
    },
  });

  return (
    <FunnelStepContainer data-testid="itineraryDetailsStep">
      <FunnelStepSection title={t("steps.itinerary_details.title")}>
        <ItineraryDetailsControl control={control} />
      </FunnelStepSection>

      <InfoBox isChat icon={<Info boxSize={5} color="primary.500" />} variant="primary" mt={2}>
        <TranslationWithHTML text={t("steps.itinerary_details.helped_5m")} />
      </InfoBox>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
